import React from "react";

const GeometryBlock = ({
block,
mode = "preview",
}) => {
const isEditor = mode === "editor";


const baseWidth =
    Number(block.width) || 320;

const baseHeight =
    Number(block.height) || 220;

const stroke =
    block.stroke || "#111111";

const strokeWidth =
    Number(block.stroke_width) || 3;

const fill =
    block.fill &&
    block.fill !== "transparent"
        ? block.fill
        : "none";

const rotation =
    Number(block.rotation) || 0;

const labels =
    block.labels || {};

const dimensions =
    block.dimensions || {};

const shapeType =
    block.shape_type || "triangle";

const labelFontSize =
    isEditor ? 15 : 11;

const dimensionFontSize =
    isEditor ? 13 : 10;

const safePadding =
    isEditor ? 55 : 25;

const width =
    baseWidth + safePadding * 2;

const height =
    baseHeight + safePadding * 2;

const xOffset =
    safePadding;

const yOffset =
    safePadding;

const commonProps = {
    stroke,
    strokeWidth,
    fill,
    strokeLinecap: "round",
    strokeLinejoin: "round",
};

const textProps = {
    fill: "#111111",
    fontFamily:
        "Arial, Helvetica, sans-serif",
    fontWeight: 600,
    pointerEvents: "none",
};

const dimensionProps = {
    fill: "#555555",
    fontFamily:
        "Arial, Helvetica, sans-serif",
    fontWeight: 500,
    pointerEvents: "none",
};

const renderText = (
    value,
    x,
    y,
    options = {}
) => {
    if (
        value === undefined ||
        value === null ||
        String(value).trim() === ""
    ) {
        return null;
    }

    return (
        <text
            x={x}
            y={y}
            textAnchor={
                options.textAnchor ||
                "middle"
            }
            dominantBaseline={
                options.dominantBaseline ||
                "middle"
            }
            fontSize={
                options.fontSize ||
                labelFontSize
            }
            {...textProps}
        >
            {String(value)}
        </text>
    );
};

const renderDimension = (
    value,
    x,
    y,
    options = {}
) => {
    if (
        value === undefined ||
        value === null ||
        String(value).trim() === ""
    ) {
        return null;
    }

    return (
        <text
            x={x}
            y={y}
            textAnchor={
                options.textAnchor ||
                "middle"
            }
            dominantBaseline={
                options.dominantBaseline ||
                "middle"
            }
            fontSize={
                options.fontSize ||
                dimensionFontSize
            }
            {...dimensionProps}
        >
            {String(value)}
        </text>
    );
};

const renderCommonLabels = () => (
    <>
        {renderText(
            labels.top,
            width / 2,
            20
        )}

        {renderText(
            labels.left,
            18,
            height / 2,
            {
                textAnchor:
                    "start",
            }
        )}

        {renderText(
            labels.right,
            width - 18,
            height / 2,
            {
                textAnchor:
                    "end",
            }
        )}

        {renderText(
            labels.bottom,
            width / 2,
            height - 20
        )}

        {renderText(
            labels.center,
            width / 2,
            height / 2
        )}
    </>
);

const renderCommonDimensions = () => (
    <>
        {renderDimension(
            dimensions.top,
            width / 2,
            38
        )}

        {renderDimension(
            dimensions.left,
            35,
            height / 2,
            {
                textAnchor:
                    "start",
            }
        )}

        {renderDimension(
            dimensions.right,
            width - 35,
            height / 2,
            {
                textAnchor:
                    "end",
            }
        )}

        {renderDimension(
            dimensions.bottom,
            width / 2,
            height - 38
        )}
    </>
);

const shapeLeft =
    xOffset;

const shapeTop =
    yOffset;

const shapeRight =
    xOffset + baseWidth;

const shapeBottom =
    yOffset + baseHeight;

const centerX =
    xOffset + baseWidth / 2;

const centerY =
    yOffset + baseHeight / 2;

return (
    <div
        style={{
            width: "100%",
            display: "flex",
            justifyContent:
                "center",
            alignItems:
                "flex-start",
            overflowX:
                "auto",
            overflowY:
                "visible",
            padding:
                isEditor
                    ? 20
                    : 8,
            boxSizing:
                "border-box",
        }}
    >
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
                display: "block",
                flexShrink: 0,
                overflow:
                    "visible",
                transform:
                    `rotate(${rotation}deg)`,
                transformOrigin:
                    "center center",
            }}
        >
            {shapeType ===
                "triangle" && (
                <>
                    <polygon
                        points={`
                            ${centerX},${shapeTop}
                            ${shapeLeft},${shapeBottom}
                            ${shapeRight},${shapeBottom}
                        `}
                        {...commonProps}
                    />

                    {renderCommonLabels()}

                    {renderCommonDimensions()}
                </>
            )}

            {shapeType ===
                "rectangle" && (
                <>
                    <rect
                        x={shapeLeft}
                        y={shapeTop}
                        width={baseWidth}
                        height={baseHeight}
                        {...commonProps}
                    />

                    {renderCommonLabels()}

                    {renderCommonDimensions()}
                </>
            )}

            {shapeType ===
                "square" && (
                <>
                    {(() => {
                        const size =
                            Math.min(
                                baseWidth,
                                baseHeight
                            );

                        const squareX =
                            xOffset +
                            (
                                baseWidth -
                                size
                            ) /
                                2;

                        const squareY =
                            yOffset +
                            (
                                baseHeight -
                                size
                            ) /
                                2;

                        return (
                            <rect
                                x={
                                    squareX
                                }
                                y={
                                    squareY
                                }
                                width={
                                    size
                                }
                                height={
                                    size
                                }
                                {...commonProps}
                            />
                        );
                    })()}

                    {renderCommonLabels()}

                    {renderCommonDimensions()}
                </>
            )}

            {shapeType ===
                "circle" && (
                <>
                    <circle
                        cx={centerX}
                        cy={centerY}
                        r={
                            Math.min(
                                baseWidth,
                                baseHeight
                            ) /
                                2
                        }
                        {...commonProps}
                    />

                    {renderCommonLabels()}

                    {renderCommonDimensions()}

                    {renderDimension(
                        block.radius,
                        centerX,
                        centerY - 20
                    )}

                    {renderDimension(
                        block.diameter,
                        centerX,
                        centerY + 20
                    )}
                </>
            )}

            {shapeType ===
                "ellipse" && (
                <>
                    <ellipse
                        cx={centerX}
                        cy={centerY}
                        rx={
                            baseWidth / 2
                        }
                        ry={
                            baseHeight / 2
                        }
                        {...commonProps}
                    />

                    {renderCommonLabels()}

                    {renderCommonDimensions()}
                </>
            )}

            {shapeType ===
                "line" && (
                <>
                    <line
                        x1={shapeLeft}
                        y1={centerY}
                        x2={shapeRight}
                        y2={centerY}
                        {...commonProps}
                    />

                    {renderCommonLabels()}

                    {renderCommonDimensions()}
                </>
            )}

            {shapeType ===
                "parallel_lines" && (
                <>
                    <line
                        x1={
                            shapeLeft
                        }
                        y1={
                            centerY -
                            baseHeight *
                                0.22
                        }
                        x2={
                            shapeRight
                        }
                        y2={
                            centerY -
                            baseHeight *
                                0.22
                        }
                        {...commonProps}
                    />

                    <line
                        x1={
                            shapeLeft
                        }
                        y1={
                            centerY +
                            baseHeight *
                                0.22
                        }
                        x2={
                            shapeRight
                        }
                        y2={
                            centerY +
                            baseHeight *
                                0.22
                        }
                        {...commonProps}
                    />

                    {renderCommonLabels()}

                    {renderCommonDimensions()}
                </>
            )}

            {shapeType ===
                "perpendicular_lines" && (
                <>
                    <line
                        x1={centerX}
                        y1={shapeTop}
                        x2={centerX}
                        y2={shapeBottom}
                        {...commonProps}
                    />

                    <line
                        x1={shapeLeft}
                        y1={centerY}
                        x2={shapeRight}
                        y2={centerY}
                        {...commonProps}
                    />

                    {renderCommonLabels()}

                    {renderCommonDimensions()}
                </>
            )}

            {shapeType ===
                "angle" && (
                <>
                    <line
                        x1={
                            shapeLeft +
                            baseWidth *
                                0.15
                        }
                        y1={
                            shapeBottom
                        }
                        x2={
                            centerX
                        }
                        y2={
                            shapeTop +
                            baseHeight *
                                0.15
                        }
                        {...commonProps}
                    />

                    <line
                        x1={
                            centerX
                        }
                        y1={
                            shapeTop +
                            baseHeight *
                                0.15
                        }
                        x2={
                            shapeRight -
                            baseWidth *
                                0.15
                        }
                        y2={
                            shapeBottom
                        }
                        {...commonProps}
                    />

                    {block.angle_label &&
                        renderText(
                            block.angle_label,
                            centerX,
                            centerY
                        )}

                    {renderCommonLabels()}

                    {renderCommonDimensions()}

                    {renderDimension(
                        block.angle
                            ? `${block.angle}°`
                            : "",
                        centerX,
                        centerY +
                            25
                    )}
                </>
            )}
        </svg>
    </div>
);


};

export default GeometryBlock;






// import React from "react";

// const GeometryBlock = ({ block }) => {
//     const width = Number(block.width) || 220;
//     const height = Number(block.height) || 150;

//     const stroke = block.stroke || "#111111";

//     const strokeWidth =
//         Number(block.stroke_width) || 2;

//     const fill =
//         block.fill &&
//         block.fill !== "transparent"
//             ? block.fill
//             : "none";

//     const rotation =
//         Number(block.rotation) || 0;

//     const commonProps = {
//         stroke,
//         strokeWidth,
//         fill,
//         strokeLinecap: "round",
//         strokeLinejoin: "round",
//     };

//     const labels =
//         block.labels || {};

//     const dimensions =
//         block.dimensions || {};

//     const labelFontSize = 12;
//     const dimensionFontSize = 11;

//     return (
//         <div
//             style={{
//                 width: "100%",
//                 display: "flex",
//                 justifyContent: "center",
//                 alignItems: "center",
//                 padding: 12,
//                 overflow: "auto",
//             }}
//         >
//             <svg
//                 width={width}
//                 height={height}
//                 viewBox={`0 0 ${width} ${height}`}
//                 style={{
//                     maxWidth: "100%",
//                     height: "auto",
//                     overflow: "visible",
//                     transform: `rotate(${rotation}deg)`,
//                     transformOrigin: "center",
//                 }}
//             >
//                 {block.shape_type === "triangle" && (
//                     <>
//                         <polygon
//                             points={`
//                                 ${width / 2},15
//                                 15,${height - 15}
//                                 ${width - 15},${height - 15}
//                             `}
//                             {...commonProps}
//                         />

//                         {labels.top && (
//                             <text
//                                 x={width / 2}
//                                 y={11}
//                                 textAnchor="middle"
//                                 fontSize={labelFontSize}
//                                 fill="#111"
//                             >
//                                 {labels.top}
//                             </text>
//                         )}

//                         {labels.left && (
//                             <text
//                                 x={15}
//                                 y={height - 3}
//                                 fontSize={labelFontSize}
//                                 fill="#111"
//                             >
//                                 {labels.left}
//                             </text>
//                         )}

//                         {labels.right && (
//                             <text
//                                 x={width - 15}
//                                 y={height - 3}
//                                 textAnchor="end"
//                                 fontSize={labelFontSize}
//                                 fill="#111"
//                             >
//                                 {labels.right}
//                             </text>
//                         )}

//                         {dimensions.top && (
//                             <text
//                                 x={width / 2}
//                                 y={height - 22}
//                                 textAnchor="middle"
//                                 fontSize={dimensionFontSize}
//                                 fill="#111"
//                             >
//                                 {dimensions.top}
//                             </text>
//                         )}
//                     </>
//                 )}

//                 {block.shape_type === "rectangle" && (
//                     <>
//                         <rect
//                             x="15"
//                             y="15"
//                             width={width - 30}
//                             height={height - 30}
//                             {...commonProps}
//                         />

//                         {labels.center && (
//                             <text
//                                 x={width / 2}
//                                 y={height / 2}
//                                 textAnchor="middle"
//                                 dominantBaseline="middle"
//                                 fontSize={labelFontSize}
//                                 fill="#111"
//                             >
//                                 {labels.center}
//                             </text>
//                         )}

//                         {dimensions.top && (
//                             <text
//                                 x={width / 2}
//                                 y={11}
//                                 textAnchor="middle"
//                                 fontSize={dimensionFontSize}
//                                 fill="#111"
//                             >
//                                 {dimensions.top}
//                             </text>
//                         )}

//                         {dimensions.left && (
//                             <text
//                                 x={8}
//                                 y={height / 2}
//                                 fontSize={dimensionFontSize}
//                                 fill="#111"
//                             >
//                                 {dimensions.left}
//                             </text>
//                         )}
//                     </>
//                 )}

//                 {block.shape_type === "square" && (
//                     <rect
//                         x="15"
//                         y="15"
//                         width={Math.min(width, height) - 30}
//                         height={Math.min(width, height) - 30}
//                         {...commonProps}
//                     />
//                 )}

//                 {block.shape_type === "circle" && (
//                     <circle
//                         cx={width / 2}
//                         cy={height / 2}
//                         r={Math.min(width, height) / 2 - 15}
//                         {...commonProps}
//                     />
//                 )}

//                 {block.shape_type === "ellipse" && (
//                     <ellipse
//                         cx={width / 2}
//                         cy={height / 2}
//                         rx={width / 2 - 15}
//                         ry={height / 2 - 15}
//                         {...commonProps}
//                     />
//                 )}

//                 {block.shape_type === "line" && (
//                     <line
//                         x1="15"
//                         y1={height / 2}
//                         x2={width - 15}
//                         y2={height / 2}
//                         {...commonProps}
//                     />
//                 )}

//                 {block.shape_type === "parallel_lines" && (
//                     <>
//                         <line
//                             x1="25"
//                             y1="45"
//                             x2={width - 25}
//                             y2="45"
//                             {...commonProps}
//                         />

//                         <line
//                             x1="25"
//                             y1={height - 45}
//                             x2={width - 25}
//                             y2={height - 45}
//                             {...commonProps}
//                         />
//                     </>
//                 )}

//                 {block.shape_type === "perpendicular_lines" && (
//                     <>
//                         <line
//                             x1={width / 2}
//                             y1="15"
//                             x2={width / 2}
//                             y2={height - 15}
//                             {...commonProps}
//                         />

//                         <line
//                             x1="15"
//                             y1={height / 2}
//                             x2={width - 15}
//                             y2={height / 2}
//                             {...commonProps}
//                         />
//                     </>
//                 )}

//                 {block.shape_type === "angle" && (
//                     <>
//                         <line
//                             x1="35"
//                             y1={height - 25}
//                             x2={width / 2}
//                             y2="25"
//                             {...commonProps}
//                         />

//                         <line
//                             x1={width / 2}
//                             y1="25"
//                             x2={width - 25}
//                             y2={height - 25}
//                             {...commonProps}
//                         />

//                         {block.angle_label && (
//                             <text
//                                 x={width / 2}
//                                 y={height / 2}
//                                 textAnchor="middle"
//                                 fontSize={labelFontSize}
//                                 fill="#111"
//                             >
//                                 {block.angle_label}
//                             </text>
//                         )}
//                     </>
//                 )}
//             </svg>
//         </div>
//     );
// };

// export default GeometryBlock;


